import React, { useEffect, useRef, useState } from 'react';
import PersonalInfoForm from '../DataCapturer/forms/PersonalInfoForm';
import ForeignDetailsForm from '../DataCapturer/forms/ForeignDetailsForm';
import InstitutionsForm from '../DataCapturer/forms/InstitutionsForm';
import AssociatesForm from '../DataCapturer/forms/AssociatesForm';

const EditPipModal = ({
  showEditModal,
  closeEditModal,
  editingPip,
  editCurrentStep,
  editCompletedSteps,
  handleEditStepClick,
  successMessage,
  savingStep,
  handleSaveStep,
  // Form states
  editFirstName,
  setEditFirstName,
  editMiddleName,
  setEditMiddleName,
  editLastName,
  setEditLastName,
  editAssociates,
  editInstitutions,
  editForeignDetails,
  editPipResultOverview,
  editValidationErrors,
  setEditValidationErrors,
  setEditPipResultOverview,
  // Handler functions
  handleEditAssociateChange,
  addEditAssociate,
  removeEditAssociate,
  handleEditInstitutionChange,
  addEditInstitution,
  removeEditInstitution,
  handleEditCountryChange,
  selectEditCountry,
  editSuggestions,
  viewMode,
  user
}) => {
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const isForeignPip = ((editingPip?.pip_type || '').toLowerCase().includes('foreign'));
  const institutionStepIndex = isForeignPip ? 2 : 1;
  const associatesStepIndex = isForeignPip ? 3 : 2;

  // Accessibility + UX: ESC to close, focus trap, body scroll lock, scroll shadow
  useEffect(() => {
    if (!showEditModal) return;

    const previouslyFocused = document.activeElement;
    const node = modalRef.current;

    // Lock background scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Do not force focus inside the modal to prevent cursor jumps while typing

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeEditModal();
      }
    };

    const onScroll = () => {
      if (!node) return;
      setScrolled(node.scrollTop > 0);
    };

    document.addEventListener('keydown', onKeyDown, true);
    if (node) node.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (node) node.removeEventListener('scroll', onScroll);
      document.body.style.overflow = originalOverflow;
      // Avoid returning focus which can disrupt typing in inputs
    };
  }, [showEditModal, closeEditModal]);

  if (!showEditModal) return null;

  // Smoothly scroll the stepper content to the bottom and focus the first input of the last section
  const scrollToNewSection = () => {
    const container = contentRef.current;
    if (!container) return;
    // Delay to allow React to render the newly added section
    setTimeout(() => {
      const sections = container.querySelectorAll('.form-section');
      const lastSection = sections[sections.length - 1];
      if (!lastSection) return;

      // Smoothly scroll near the new section without altering focus
      const sectionTop = lastSection.offsetTop;
      const desiredTop = Math.max(0, sectionTop - 20);
      container.scrollTo({ top: desiredTop, behavior: 'smooth' });
    }, 120);
  };

  // Helper function to get steps based on PIP type
  const getEditSteps = () => {
    const baseSteps = [
      { label: 'Personal Info', description: 'PIP personal information' },
      { label: 'Institutions', description: 'Institution affiliations' },
      { label: 'Associates', description: 'Related associates' }
    ];

    if (isForeignPip) {
      baseSteps.splice(1, 0, { label: 'Foreign Details', description: 'Country and additional notes' });
    }

    return baseSteps;
  };

  // Render step content based on current step
  const StepSaveButton = ({ label, stepIndex }) => {
    if (viewMode) return null;
    return (
      <div className="step-inline-save">
        <button
          type="button"
          className="step-button save"
          onClick={() => handleSaveStep(stepIndex)}
          disabled={savingStep}
        >
          {savingStep ? 'Updating...' : `Update ${label}`}
        </button>
      </div>
    );
  };

  const renderEditStepContent = () => {
    switch (editCurrentStep) {
      case 0:
        return (
          <>
            <PersonalInfoForm
              pipType={editingPip?.pip_type}
              firstName={editFirstName}
              setFirstName={setEditFirstName}
              middleName={editMiddleName}
              setMiddleName={setEditMiddleName}
              lastName={editLastName}
              setLastName={setEditLastName}
              pipResultOverview={editPipResultOverview}
              setPipResultOverview={setEditPipResultOverview}
              validationErrors={editValidationErrors}
              setValidationErrors={setEditValidationErrors}
              viewMode={viewMode}
              user={user}
              actions={<StepSaveButton label="Personal Info" stepIndex={0} />}
            />
          </>
        );

      case 1:
        if (isForeignPip) {
          return (
            <>
              <ForeignDetailsForm
                foreignDetails={editForeignDetails}
                setForeignDetails={() => {}}
                handleCountryChange={handleEditCountryChange}
                selectCountry={selectEditCountry}
                suggestions={editSuggestions}
                validationErrors={editValidationErrors}
                viewMode={viewMode}
                actions={<StepSaveButton label="Foreign Details" stepIndex={1} />}
              />
            </>
          );
        }
        return (
          <>
            <InstitutionsForm
              institutions={editInstitutions}
              handleInstitutionChange={handleEditInstitutionChange}
              addInstitution={() => {
                addEditInstitution();
                scrollToNewSection();
              }}
              removeInstitution={removeEditInstitution}
              validationErrors={editValidationErrors}
              viewMode={viewMode}
              actions={<StepSaveButton label="Institutions" stepIndex={institutionStepIndex} />}
            />
          </>
        );

      case isForeignPip ? 2 : 1:
        if (isForeignPip && editCurrentStep === 2) {
          return (
            <>
              <InstitutionsForm
                institutions={editInstitutions}
                handleInstitutionChange={handleEditInstitutionChange}
                addInstitution={() => {
                  addEditInstitution();
                  scrollToNewSection();
                }}
                removeInstitution={removeEditInstitution}
                validationErrors={editValidationErrors}
                viewMode={viewMode}
                actions={<StepSaveButton label="Institutions" stepIndex={institutionStepIndex} />}
              />
            </>
          );
        }
        // Fall through to associates

      case isForeignPip ? 3 : 2:
        return (
          <>
            <AssociatesForm
              associates={editAssociates}
              handleAssociateChange={handleEditAssociateChange}
              addAssociate={() => {
                addEditAssociate();
                scrollToNewSection();
              }}
              removeAssociate={removeEditAssociate}
              viewMode={viewMode}
              actions={<StepSaveButton label="Associates" stepIndex={associatesStepIndex} />}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay open" onClick={closeEditModal}>
      <div
        className="modal-content condensed"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-pip-modal-title"
        ref={modalRef}
        style={{
          width: 'min(1100px, max(320px, calc(100vw - var(--sidebar-width, 220px) - 64px)))',
          maxWidth: 'max(320px, calc(100vw - var(--sidebar-width, 220px) - 64px))',
          maxHeight: '90vh',
          borderRadius: 10,
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)'
        }}
      >
        <div className="modal-header" style={{ boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>
          <h2 id="edit-pip-modal-title">{viewMode ? 'PIP Details' : 'Edit PIP Information'}</h2>
          <button className="modal-close" onClick={closeEditModal}>×</button>
        </div>
        
        <div className="stepper-container" style={{ marginBottom: 8 }}>
          <div className="stepper-header">
            {getEditSteps().map((step, index) => (
              <div
                key={index}
                className={`stepper-step ${
                  index === editCurrentStep ? 'active' : ''
                } ${editCompletedSteps.includes(index) ? 'completed' : ''}`}
                onClick={() => handleEditStepClick(index)}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              >
                <div className="step-number">
                  {editCompletedSteps.includes(index) ? '✓' : index + 1}
                </div>
                <div className="step-label">{step.label}</div>
              </div>
            ))}
          </div>
          
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ width: `${((editCurrentStep + 1) / getEditSteps().length) * 100}%` }}
            />
          </div>

          <div className="stepper-content" ref={contentRef}>
            {successMessage && !viewMode && (
              <div style={{
                padding: '10px 15px',
                marginBottom: '15px',
                backgroundColor: 'var(--success-bg)',
                color: 'var(--success)',
                border: '1px solid var(--success-border)',
                borderRadius: '4px'
              }}>
                {successMessage}
              </div>
            )}
            {renderEditStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPipModal;